/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { createContext, Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';
import useLoanDefaultCodesInfo from '../common/hooks/useLoanDefaultCodesInfo';
import { useLoanContext } from './LoanContext';
import { useAuthContext } from './AuthContext';
import { schema } from '../validation/LoanDefaultCodesSchema';
import {
  convertDateInputTo8AMUTC,
  formatDateYYYYMMDD,
  getDefaultReportingDate,
  getErrorStr,
  isApplicableForLoanType,
  isDateInputPastOrCurrent,
  isReportingDateValid,
  isStrEmptyOrNull,
} from '../common/UtilityFunctions';
import { useSnackbar } from 'notistack';

type LDCFunctionsType = {
  handleLDCChange: (action: string, name: string, value?: any) => void;
  checkReportingDate: (e: React.FocusEvent<HTMLInputElement>, investor?: boolean) => void;
  allowOnlyCurrentPastDates: (e: React.FocusEvent<HTMLInputElement>, label: string, name: string) => void;
  checkEstimatedForbearanceDates: (e: React.FocusEvent<HTMLInputElement>, label: string, name: string) => void;
  getBKStatusAbbrev: () => void;
  editDDSvars: (investor?: boolean) => void;
  enqueueReportingDateErrors: (reportingDateError: string) => void;
  handleErrorsSeen: () => void;
  setEditingInProcess: Dispatch<SetStateAction<boolean>>;
  handleDateOnBlur: (
    e: React.FocusEvent<HTMLInputElement>,
    label: string,
    name: string,
    onblurfunc?: () => void,
  ) => void;
  handleSave: () => void;
  handleCancelEdit: () => void;
  handleEdit: () => void;
  editCutoffActionVars: (e: React.FocusEvent<HTMLInputElement>) => void;
  cancelDDSVars: (investor: boolean, e?: React.FocusEvent<HTMLInputElement, Element>) => void;
  updateLDCInfo: () => Promise<void>;
  cancelLDCChanges: () => void;
};

type LDCStatusesType = {
  errorLoading: string;
  errorSaving: string;
  errorEndpoint: string;
  errorValidating: string;
  loading: boolean;
  hasChanges: boolean;
};

type LDCUIStatuses = {
  showEdit: boolean;
  showSaveCancel: boolean;
  errorsSeen: boolean;
  showReload: boolean;
  isEditable: boolean;
  editingInProcess: boolean;
};

type LDCContextType = {
  LDCInfo: any;
  LDCStatus: LDCStatusesType;
  LDCUIStatuses: LDCUIStatuses;
  loanNumber: string;
  loanPropCode: string;
  LDCFunctions: LDCFunctionsType;
  invReportUseColToggled: boolean;
  actionsColToggled: boolean;
  gnmaColToggled: boolean;
  noInvestorCols: boolean;
  filteredDDSCodeOptions: any;
  filteredDDRCodeOptions: any;
  filteredServicingOccupancyOptions: any;
};

const LDCContext = createContext<LDCContextType | undefined>(undefined);

const useLDCContext = () => {
  const ldcContext = useContext(LDCContext);
  if (ldcContext === undefined) {
    /// throwing this error helps in development when the context is out of scope.
    throw new Error('LDCContext must be inside a LDCContextProvider');
  }
  return ldcContext;
};

const LDCProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    loanDefaultInfo: LDCInfo,
    apis: LDCApis,
    statuses: LDCStatuses,
    handleChange: handleLDCChange,
  } = useLoanDefaultCodesInfo();
  const { enqueueSnackbar } = useSnackbar();
  const { loanNumber, propCode: loanPropCode } = useLoanContext();
  const { fullAccess } = useAuthContext();
  const [errorsSeen, setErrorsSeen] = useState(false);
  const [editingInProcess, setEditingInProcess] = useState(false);

  const { getLDCInfo, updateLDCInfo } = LDCApis;

  const { errorLoading, errorSaving, errorEndpoint, loading, hasChanges } = LDCStatuses;
  const { bkStatus, investorLoanTypeKeyword, investorLoanType, investorCode, fhlmcCutoffActionCode } = LDCInfo;

  const [errorValidating, setErrorValidating] = useState('');

  const { BankruptcyStatusOptions, ddrReasonOptions, DDSCodeOptions, ServicingOccupancyOptions } = LDCInfo.QIDOptions;
  const { EstimatedForbearanceStartDate, EstimatedForbearanceEndDate, CutoffActionDate, CutoffActionReportingDate } =
    LDCInfo.dates;

  const filteredDDSCodeOptions = useMemo(
    () => DDSCodeOptions.filter((element) => isApplicableForLoanType(investorLoanType, element)),
    [investorLoanType, DDSCodeOptions],
  );

  const filteredDDRCodeOptions = useMemo(
    () => ddrReasonOptions.filter((element) => isApplicableForLoanType(investorLoanType, element)),
    [investorLoanType, ddrReasonOptions],
  );

  const filteredServicingOccupancyOptions = useMemo(
    () => ServicingOccupancyOptions.filter((element) => isApplicableForLoanType(investorLoanType, element)),
    [investorLoanType, ServicingOccupancyOptions],
  );

  const invReportUseColToggled = investorLoanTypeKeyword.startsWith('FHLMC');
  const actionsColToggled = investorLoanTypeKeyword.startsWith('FHLMC') || investorLoanTypeKeyword.startsWith('FNMA');
  const gnmaColToggled = ['331', '332', '333'].includes(investorCode);
  const noInvestorCols = !invReportUseColToggled && !actionsColToggled && !gnmaColToggled;

  // editing, saving, retry
  const [isEditable, setIsEditable] = useState(false);
  const showSaveCancel = isEditable && errorLoading === '' && errorSaving === '' && !loading;
  const showEdit =
    !isEditable &&
    errorLoading === '' &&
    errorSaving === '' &&
    !loading &&
    loanNumber !== '' &&
    loanNumber !== null &&
    fullAccess;
  const showReload = (errorLoading !== '' || errorSaving !== '') && !loading;

  const cancelDDSVars = useCallback(
    (investor: boolean, e = null) => {
      handleLDCChange('cancel', investor ? 'investorDDSCode' : 'DDSCode');
      handleLDCChange('cancel', investor ? 'investorDDSRefQid' : 'DDSRefQid');
      handleLDCChange('cancel', investor ? 'investorDDSEventKwd' : 'DDSEventKwd');
      if (e !== null) e.target.value = null;
      handleLDCChange('cancel', investor ? 'dates.investorDDSReportingDate' : 'dates.DDSReportingDate');
      handleLDCChange('cancel', investor ? 'dates.investorDDSEventDate' : 'dates.DDSEventDate');
    },
    [handleLDCChange],
  );

  const cancelLDCChanges = useCallback(() => {
    handleLDCChange('cancel', '');
  }, [handleLDCChange, cancelDDSVars]);

  const handleCancelEdit = useCallback(() => {
    setIsEditable(false);
    cancelLDCChanges();
  }, [cancelLDCChanges]);

  const enqueueReportingDateErrors = useCallback(
    (reportingDateError: string) => {
      if (reportingDateError === 'empty') {
        enqueueSnackbar('Reporting date must be filled out to add a DDS code', { variant: 'error' });
      } else if (reportingDateError === 'invalid') {
        enqueueSnackbar('Reporting date can only be the first of the last month, current month, or any future month.', {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar],
  );

  const checkReportingDate = useCallback(
    (e: React.FocusEvent<HTMLInputElement>, investor: boolean = false) => {
      let reportingDateEmpty = e.target.value === null || e.target.value === '';
      let reportingDateInvalid = !reportingDateEmpty && !isReportingDateValid(e);
      let reportingDateError = reportingDateEmpty ? 'empty' : '';
      if (isStrEmptyOrNull(reportingDateError) && reportingDateInvalid) {
        reportingDateError = 'invalid';
      }
      enqueueReportingDateErrors(reportingDateError);
      if (!isStrEmptyOrNull(reportingDateError)) {
        cancelDDSVars(investor, e);
      }
    },
    [cancelDDSVars, enqueueReportingDateErrors],
  );

  const allowOnlyCurrentPastDates = useCallback(
    (e: React.FocusEvent<HTMLInputElement>, label: string, name: string) => {
      if (!isStrEmptyOrNull(e.target.value) && !isDateInputPastOrCurrent(e.target.value)) {
        e.target.value = null;
        handleLDCChange('edit', name, null);
        enqueueSnackbar(label + ' must be a past or current date.', { variant: 'error' });
      }
    },
    [enqueueSnackbar, handleLDCChange],
  );

  const getBKStatusAbbrev = useCallback(() => {
    if (bkStatus !== '' && Object.keys(BankruptcyStatusOptions).length !== 0) {
      if (bkStatus in BankruptcyStatusOptions) {
        if (bkStatus !== BankruptcyStatusOptions[bkStatus]) {
          handleLDCChange('save', 'bkStatus', BankruptcyStatusOptions[bkStatus]);
        }
      }
    }
  }, [bkStatus, BankruptcyStatusOptions, handleLDCChange]);

  const checkEstimatedForbearanceDates = useCallback(
    (e: React.FocusEvent<HTMLInputElement>, label: string, name: string) => {
      if (
        name === 'dates.EstimatedForbearanceEndDate' &&
        e.target.value !== '' &&
        !isStrEmptyOrNull(EstimatedForbearanceStartDate)
      ) {
        if (convertDateInputTo8AMUTC(e.target.value) <= convertDateInputTo8AMUTC(EstimatedForbearanceStartDate)) {
          e.target.value = null;
          handleLDCChange('edit', name, null);
          enqueueSnackbar(label + ' must be after Estimated Forbearance Start Date', { variant: 'error' });
        }
      }
      if (
        name === 'dates.EstimatedForbearanceStartDate' &&
        e.target.value !== '' &&
        !isStrEmptyOrNull(EstimatedForbearanceEndDate)
      ) {
        if (convertDateInputTo8AMUTC(e.target.value) >= convertDateInputTo8AMUTC(EstimatedForbearanceEndDate)) {
          e.target.value = null;
          handleLDCChange('edit', name, null);
          enqueueSnackbar(label + ' must be before Estimated Forbearance End Date', { variant: 'error' });
        }
      }
    },
    [EstimatedForbearanceStartDate, EstimatedForbearanceEndDate, enqueueSnackbar, handleLDCChange],
  );

  const editDDSvars = useCallback(
    (investor: boolean = false) => {
      const qidVarName = investor ? 'investorDDSRefQid' : 'DDSRefQid';
      const qidVarVal = investor ? 'INVDDSCODE24' : 'DDSCODE23';
      const eventVarName = investor ? 'investorDDSEventKwd' : 'DDSEventKwd';
      const eventKwdVal = investor ? 'INV DDS CODE' : 'DDS CODE';
      const reportingDateVarName = investor ? 'dates.investorDDSReportingDate' : 'dates.DDSReportingDate';
      handleLDCChange('edit', qidVarName, qidVarVal);
      handleLDCChange('edit', eventVarName, eventKwdVal);
      let reportingDate = getDefaultReportingDate();
      let reportingDateFormatted = formatDateYYYYMMDD(reportingDate);
      handleLDCChange('edit', reportingDateVarName, reportingDateFormatted);
    },
    [handleLDCChange],
  );

  const editCutoffActionVars = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (investorLoanTypeKeyword.startsWith('FHLMC')) {
        handleLDCChange('edit', 'CutoffActionRefQid', 'MASZ_CD.032');
        handleLDCChange('edit', 'CutoffActionCode', fhlmcCutoffActionCode);
        if (isStrEmptyOrNull(CutoffActionReportingDate)) {
          handleLDCChange('edit', 'dates.CutoffActionReportingDate', CutoffActionDate);
        }
      }
    },
    [handleLDCChange, investorLoanTypeKeyword, CutoffActionDate, fhlmcCutoffActionCode],
  );

  const handleDateOnBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>, label: string, name: string, onblurfunc = null) => {
      setEditingInProcess(false);
      if (e.target.value === '') {
        e.target.value = null;
      }
      if (onblurfunc !== null) {
        onblurfunc(e, label, name);
      }
    },
    [],
  );

  const handleSave = useCallback(async () => {
    setIsEditable(false);

    schema
      .validate(LDCInfo, { abortEarly: false })
      .then(async (valid) => {
        await updateLDCInfo();
        setErrorsSeen(false);
      })
      .catch((error) => {
        setErrorValidating(getErrorStr(error.errors));
        console.log('Validation failed:', error.errors);
      });
  }, [LDCInfo, updateLDCInfo]);

  const handleRetryRetrieving = useCallback(async () => {
    await getLDCInfo();
    setErrorsSeen(false);
  }, [getLDCInfo]);

  const handleEdit = useCallback(() => {
    setIsEditable(true);
  }, []);

  const handleErrorsSeen = useCallback(() => {
    if (showReload) {
      handleRetryRetrieving();
    } else {
      if (errorValidating != '') {
        setIsEditable(true);
        setErrorValidating('');
      } else {
        setErrorsSeen(true);
      }
    }
  }, [showReload, handleRetryRetrieving, errorValidating]);

  const LDCFunctions = {
    handleLDCChange,
    getBKStatusAbbrev,
    editDDSvars,
    checkReportingDate,
    allowOnlyCurrentPastDates,
    checkEstimatedForbearanceDates,
    enqueueReportingDateErrors,
    handleErrorsSeen,
    setEditingInProcess,
    handleDateOnBlur,
    editCutoffActionVars,
    handleSave,
    handleCancelEdit,
    handleEdit,
    cancelDDSVars,
    updateLDCInfo,
    cancelLDCChanges,
  };

  const LDCStatus = { errorLoading, errorSaving, errorEndpoint, loading, hasChanges, errorValidating };
  const LDCUIStatuses = {
    showEdit,
    showSaveCancel,
    errorsSeen,
    showReload,
    isEditable,
    editingInProcess,
  };

  return (
    <LDCContext.Provider
      value={{
        LDCInfo,
        LDCStatus,
        LDCFunctions,
        LDCUIStatuses,
        loanNumber,
        loanPropCode,
        invReportUseColToggled,
        actionsColToggled,
        gnmaColToggled,
        noInvestorCols,
        filteredDDSCodeOptions,
        filteredDDRCodeOptions,
        filteredServicingOccupancyOptions,
      }}
    >
      {children}
    </LDCContext.Provider>
  );
};

export { LDCProvider, useLDCContext };
